// The app: signed out, locked, or the Library and projects of an unlocked browser.
import { forgetLocalPreferences } from '@writing-suite/client/browser/forget.ts';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../rooms/index.ts'; // every room's pages, registered before the first route is read
import { HelpPage } from '../help/HelpPage.tsx';
import { host, type Host } from '../host/host.ts';
import { boot, endSession, isOnline, sessionEnded, unlocked, type Account, type Boot } from '../store/account.ts';
import { fetchProjects, fetchShares, mergeShares, openProjects, openShares, type LibraryProject, type PendingShare } from '../store/library.ts';
import { takeOpenedFiles } from '../store/openedFiles.ts';
import { ExportPage } from '../views/ExportPage.tsx';
import { HostCheck } from '../views/HostCheck.tsx';
import { Library } from '../views/Library.tsx';
import { ProfilePage } from '../views/ProfilePage.tsx';
import { SettingsPage } from '../views/SettingsPage.tsx';
import { AppContext, useOnline, type AppState } from './context.ts';
import { Gate, Opening } from './Gate.tsx';
import { ProjectShell } from './ProjectShell.tsx';
import { navigate, useRoute } from './route.ts';

export function App() {
  const route = useRoute();
  const [state, setState] = useState<Boot | null>(null);
  useEffect(() => {
    let cancelled = false;
    boot()
      .then((b) => !cancelled && setState(b))
      .catch(() => !cancelled && setState({ kind: 'offlineLocked' }));
    return () => {
      cancelled = true;
    };
  }, []);

  // Help and the host self-check read without an account.
  if (route.name === 'help' && state?.kind !== 'ready') return <HelpPage article={route.article} />;
  if (route.name === 'hostCheck') return <HostCheck account={state?.kind === 'ready' ? state.account : null} />;
  if (!state) return <Opening />;
  if (state.kind === 'ready') {
    return (
      <SignedIn
        key={state.account.accountId}
        account={state.account}
        onEnded={(message) => setState({ kind: 'signedOut', message })}
      />
    );
  }
  return (
    <Gate
      state={state}
      onUnlocked={() => state.kind === 'locked' && setState(unlocked(state.session, state.backend))}
      onRetry={() => {
        setState(null);
        void boot().then(setState);
      }}
    />
  );
}

function SignedIn({ account, onEnded }: { account: Account; onEnded: (message: string) => void }) {
  const online = useOnline();
  const route = useRoute();
  const [projects, setProjects] = useState<LibraryProject[] | null>(null);
  const [pendingShares, setPendingShares] = useState<PendingShare[]>([]);
  const [listFromCache, setListFromCache] = useState(account.startedOffline);
  const ended = useRef(false);

  const endedHere = useCallback(() => {
    if (ended.current) return;
    ended.current = true;
    void endSession(account).then(() => onEnded('This browser was signed out. Sign in again to read your projects.'));
  }, [account, onEnded]);

  /** The account's own projects and those shared with it (W9a), opened; `api` null offline. */
  const openAll = useCallback(
    async (own: Parameters<typeof openProjects>[0], shares: Parameters<typeof openShares>[0], api: typeof account.session.api | null) => {
      const mine = await openProjects(own, account.accountId, account.avk, account.store);
      const shared = await openShares(shares, {
        api, accountId: account.accountId, avk: account.avk, store: account.store,
        exchange: () => account.session.exchangeKey().catch(() => null),
      });
      return { projects: [...mine, ...shared.projects], pending: shared.pending, shares: shared.shares };
    },
    [account],
  );

  const reopen = useCallback(async () => {
    const cached = await account.store.projects();
    const shares = (await account.store.shares())?.shares ?? [];
    if (!cached && !shares.length) return;
    const opened = await openAll(cached?.projects ?? [], shares, null);
    setProjects(opened.projects);
    setPendingShares(opened.pending);
  }, [account, openAll]);

  const refreshProjects = useCallback(async () => {
    await reopen();
    if (!isOnline()) {
      setListFromCache(true);
      setProjects((p) => p ?? []);
      return;
    }
    try {
      const [list, fresh] = await Promise.all([fetchProjects(account.session.api), fetchShares(account.session.api).catch(() => null)]);
      await account.store.putProjects(list);
      const kept = (await account.store.shares())?.shares ?? null;
      const shares = fresh ? mergeShares(kept, fresh) : (kept ?? []);
      const opened = await openAll(list, shares, account.session.api);
      // Kept as opened: an AVK copy stored on the way is remembered for the next offline start.
      await account.store.putShares(opened.shares);
      setProjects(opened.projects);
      setPendingShares(opened.pending);
      setListFromCache(false);
    } catch (e) {
      // Unreachable (or refused): the list this computer kept stands, and the page says it's offline.
      if (sessionEnded(e)) return endedHere();
      setListFromCache(true);
      setProjects((p) => p ?? []);
    }
  }, [account, reopen, endedHere, openAll]);

  useEffect(() => {
    void refreshProjects();
  }, [refreshProjects]);
  // An account without an exchange key (made before shared projects) gets one at its first unlocked start (W9a).
  useEffect(() => {
    if (!account.startedOffline) void account.session.exchangeKey().catch(() => {});
  }, [account]);
  // Back online: the list again, and (in each open project) the socket.
  useEffect(() => {
    if (online && listFromCache) void refreshProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const signOut = useCallback(async () => {
    ended.current = true;
    await account.session.signOut();
    await account.store.wipe().catch(() => {});
    // Carets, last pages, filters and every per-project setting go too; only the browser's look stays.
    forgetLocalPreferences();
    navigate('/', { replace: true });
    onEnded('Signed out. Your keys and the copies of your projects have been removed from this browser.');
  }, [account, onEnded]);

  const noteProject = useCallback(
    (id: string) => {
      void account.store.info(id).then((info) => setProjects((list) => list?.map((p) => (p.id === id ? { ...p, info } : p)) ?? list));
    },
    [account],
  );

  // A native shell closing its window asks what would be lost first (host/host.ts).
  useEffect(() => {
    let found: Host | null = null;
    void host().then((h) => {
      found = h;
      h.setUnsentCounter(() => account.store.unsent());
    });
    return () => found?.setUnsentCounter(null);
  }, [account]);

  // Files the writer opened with a native shell (Five Rooms for Windows' file types), at start and while it runs: read
  // and queued for the Library, which offers to import them (store/openedFiles.ts).
  useEffect(() => {
    let stopped = false;
    let off: (() => void) | null = null;
    void host().then((h) => {
      if (stopped || !h.has('files.open')) return;
      const take = () => void takeOpenedFiles(h).then((some) => {
        if (some && !stopped) navigate('/');
      });
      take();
      off = h.onFilesOpened(take);
    });
    return () => {
      stopped = true;
      off?.();
    };
  }, []);

  // Old URLs (and a room without its page) go where they lead now, without a history entry.
  useLayoutEffect(() => {
    if (route.name === 'redirect') navigate(route.to, { replace: true });
  }, [route]);

  const app: AppState = useMemo(
    () => ({ account, online, projects, pendingShares, listFromCache, refreshProjects, noteProject, sessionEnded: endedHere, signOut }),
    [account, online, projects, pendingShares, listFromCache, refreshProjects, noteProject, endedHere, signOut],
  );

  return (
    <AppContext.Provider value={app}>
      {route.name === 'redirect' ? null : route.name === 'help' ? (
        <HelpPage article={route.article} />
      ) : route.name === 'project' ? (
        <ProjectShell key={route.projectId} route={route} />
      ) : route.name === 'library' ? (
        <Library />
      ) : route.name === 'export' ? (
        <ExportPage />
      ) : route.name === 'settings' ? (
        <SettingsPage />
      ) : route.name === 'profile' ? (
        <ProfilePage />
      ) : route.name === 'hostCheck' ? (
        <HostCheck account={account} />
      ) : (
        <Library missing={route.path} />
      )}
    </AppContext.Provider>
  );
}
